/**
 * useModelCatalog · 统一模型目录数据源（workflow 编辑器用）
 *
 * TODO: [useApiQuery 迁移] _load 方法的 loading/try/catch/finally 模板代码可用 useApiQuery 声明式替换
 *
 * 目的：让 LLM / QuestionClassifier / ParameterExtractor 等节点的模型选择器
 * 引用"模型管理"里集中配置的 CatalogModel 列表，而不是用户随便输入文本。
 *
 * 实现：
 * - 首次调用拉 /model-config/catalog，进程级缓存
 * - 支持手动 refresh()
 * - 拉取失败时返回空数组 + error，由调用方降级为 text input
 */
import { ref, shallowRef, readonly, type Ref } from 'vue'
import type { CatalogModel } from '@/api/modelConfig'
import { fetchModelCatalog } from '@/api/modelConfig'

// ── 进程级缓存 ───────────────────────────────────────────
const _models = shallowRef<CatalogModel[]>([])
const _loading = ref(false)
const _error = ref<Error | null>(null)
let _inflight: Promise<CatalogModel[]> | null = null
let _loadedOnce = false

async function _load(force = false): Promise<CatalogModel[]> {
  // 已加载 + 非强制 → 直接返回
  if (_loadedOnce && !force) {
    return _models.value
  }
  // 正在加载 → 复用 inflight（避免并发重复请求）
  if (_inflight) {
    return _inflight
  }
  _loading.value = true
  _error.value = null
  _inflight = (async () => {
    try {
      const data = await fetchModelCatalog({ available_only: false })
      // 去重：按 provider::model_id 复合键，后端出异常重复时前端兜底。
      _models.value = Array.isArray(data) ? _dedupeByProviderAndId(data) : []
      _loadedOnce = true
      return _models.value
    } catch (err) {
      _error.value = err instanceof Error ? err : new Error(String(err))
      // 失败时保留旧数据（如果有），不清空
      return _models.value
    } finally {
      _loading.value = false
      _inflight = null
    }
  })()
  return _inflight
}

/** 按 (provider, model_id) 复合键去重，保留首次出现顺序。 */
function _dedupeByProviderAndId(items: CatalogModel[]): CatalogModel[] {
  const seen = new Set<string>()
  const out: CatalogModel[] = []
  for (const m of items) {
    const key = `${m.provider ?? ''}::${m.model_id ?? ''}`
    if (!key || key === '::') continue
    if (seen.has(key)) continue
    seen.add(key)
    out.push(m)
  }
  return out
}

export interface UseModelCatalogReturn {
  /** 只读模型列表（shallowRef，避免深度响应性开销） */
  models: Readonly<Ref<readonly CatalogModel[]>>
  /** 正在拉取中 */
  loading: Readonly<Ref<boolean>>
  /** 最近一次错误（null 表示无错误或已成功过） */
  error: Readonly<Ref<Error | null>>
  /** 主动刷新（从后端重新拉） */
  refresh: () => Promise<CatalogModel[]>
  /** 按 model_id 查找（便于 panel 拿到选中项的完整元信息） */
  findById: (id: string) => CatalogModel | undefined
  /** 按 provider + name 查找（panel 从 config 反查选中项） */
  findByProviderAndName: (provider: string, name: string) => CatalogModel | undefined
}

/**
 * 使用模型目录。
 *
 * @param autoLoad 是否在首次调用时自动拉取（默认 true）。
 *   false 时只返回当前缓存，由调用方决定何时 refresh()。
 */
export function useModelCatalog(autoLoad = true): UseModelCatalogReturn {
  if (autoLoad && !_loadedOnce && !_inflight) {
    // fire-and-forget；UI 先看空列表，再在数据到达后响应式更新
    void _load(false)
  }

  return {
    models: readonly(_models) as unknown as Readonly<Ref<readonly CatalogModel[]>>,
    loading: readonly(_loading),
    error: readonly(_error),
    refresh: () => _load(true),
    findById: (id: string) => {
      const matches = _models.value.filter((m) => m.model_id === id)
      return matches.length === 1 ? matches[0] : undefined
    },
    findByProviderAndName: (provider: string, name: string) =>
      _models.value.find((m) => m.provider === provider && m.model_id === name),
  }
}
